import { AnimatePresence, motion } from 'motion/react';

import { Icon } from '~/modules/elements/akui/icon';
import styles from '~/modules/game-engine/drawing/styles';
import events from '~/modules/game-events/game-events';
import { useEventListener } from '~/modules/game-events/hooks';
import { useSubscription } from '~/modules/remote-mic/network/client/hooks/use-subscription';
import { HelpEntry, KeyboardLayoutMode } from '~/routes/keyboard-help/context';
import { assertNever, ControlGroup, renderableControls } from '~/routes/keyboard-help/controls';
import { remoteButtonIcons } from '~/routes/keyboard-help/remote-button-icons';
import { MAIN_MENU_TITLE, MIC_SETUP_TITLE, SONG_SETTINGS_TITLE } from '~/routes/keyboard-help/screen-titles';
import MainMenuScreen from '~/routes/remote-mic/panels/microphone/main-menu-screen';
import MicSetupScreen from '~/routes/remote-mic/panels/microphone/mic-setup-screen';
import OnTelevision from '~/routes/remote-mic/panels/microphone/on-television';
import { PhonePhoto } from '~/routes/remote-mic/panels/microphone/phone-photo';
import { activateControl, onPress } from '~/routes/remote-mic/panels/microphone/remote-actions';
import RemoteControl from '~/routes/remote-mic/panels/microphone/remote-controls';
import { mirrorDrawsItsOwnMicCircle } from '~/routes/remote-mic/panels/microphone/screen-shows-level';
import { screenTransition } from '~/routes/remote-mic/panels/microphone/screen-transition';
import SingingScreen from '~/routes/remote-mic/panels/microphone/singing-screen';
import SongSelectionScreen from '~/routes/remote-mic/panels/microphone/song-selection-screen';
import SongSettingsScreen from '~/routes/remote-mic/panels/microphone/song-settings-screen';

interface Props {
  isMicOn: boolean;
}

// Keys the AnimatePresence swap. Mirror screens are told apart by `title` - the only per-screen
// identity the remote gets; classic and song-selection have no such distinction.
function keyboardKey(mode: KeyboardLayoutMode, keyboard: HelpEntry) {
  return mode === 'mirror' ? `mirror:${keyboard.title ?? ''}` : mode;
}

export default function RemoteMicKeyboard({ isMicOn }: Props) {
  const keyboard = useSubscription('keyboard-layout');
  const currentSong = useSubscription('current-song');

  // A song is running and the TV is not showing one of its own overlays, so the phone is simply the
  // microphone. That state registers a keyboard with no title, which is why the song has to be
  // asked about separately rather than read off the layout.
  if (currentSong?.started && keyboard?.mode !== 'mirror') {
    return (
      <motion.div
        key="singing"
        data-test="remote-keyboard"
        data-mode="singing"
        className="flex h-full min-h-0 w-full flex-col gap-3"
        {...screenTransition}>
        <SingingScreen
          song={currentSong}
          isMicOn={isMicOn}
          onPause={onPress('back')}
          pauseEnabled={keyboard?.back !== undefined}
        />
      </motion.div>
    );
  }

  if (keyboard === undefined) return null;

  const mode: KeyboardLayoutMode = keyboard.mode ?? 'classic';

  return (
    <AnimatePresence mode="wait" initial={false}>
      {renderKeyboard(mode, keyboard)}
    </AnimatePresence>
  );
}

function renderKeyboard(mode: KeyboardLayoutMode, keyboard: HelpEntry) {
  const key = keyboardKey(mode, keyboard);

  switch (mode) {
    case 'mirror':
      return renderMirror(key, keyboard);
    case 'song-selection':
      return <SongSelectionScreen key={key} keyboard={keyboard} classic={false} />;
    case 'classic':
      return <SongSelectionScreen key={key} keyboard={keyboard} classic />;
    default:
      return assertNever(mode);
  }
}

// These screens have a phone layout of their own; every other mirrored screen is drawn as its list of
// controls.
function renderMirror(key: string, keyboard: HelpEntry) {
  switch (keyboard.title) {
    case MAIN_MENU_TITLE:
      return <MainMenuScreen key={key} keyboard={keyboard} />;
    case SONG_SETTINGS_TITLE:
      return <SongSettingsScreen key={key} keyboard={keyboard} />;
    case MIC_SETUP_TITLE:
      return <MicSetupScreen key={key} keyboard={keyboard} />;
    default:
      return <MirrorKeyboard key={key} keyboard={keyboard} />;
  }
}

function ScreenHeader({
  icon,
  title,
  subtitle,
}: {
  icon: Parameters<typeof Icon>[0]['icon'];
  title: string;
  subtitle?: string;
}) {
  return (
    <div data-test="remote-keyboard-header" className="flex shrink-0 flex-col gap-0.5">
      <div className="flex items-center gap-2.5">
        <Icon icon={icon} size={5.5} className="shrink-0 text-white/60" />
        <span className="min-w-0 flex-1 truncate text-[1.375rem] font-bold">{title}</span>
      </div>
      {/* Indented past the glyph so it hangs off the title rather than off the edge: the icon is
          22px at 5.5 and the gap 10px, which is the 2rem. */}
      {subtitle && (
        <span data-test="remote-keyboard-subtitle" className="text-active truncate pl-8 text-[0.9375rem]">
          {subtitle}
        </span>
      )}
    </div>
  );
}

// Past this many mirrored controls the screen stops fitting a phone, which is when the header and a
// short leading card stop being rows you scroll past and start being rows that hold.
const CROWDED_CONTROLS = 6;
// The most rows a leading card may hold and still be held. Settings has four, and five keeps it held
// when Advanced opens its one extra setting.
const HELD_CARD_ROWS = 5;

/** A run of value controls sharing one card with hairline dividers. */
function ValueCard({ controls }: { controls: HelpEntry['controls'] }) {
  return (
    <div
      data-test="mirror-value-card"
      className="flex shrink-0 flex-col overflow-hidden rounded-[1.125rem] bg-black/55 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-white/10">
      {controls?.map((control) => (
        <RemoteControl key={control.name} control={control} onActivate={activateControl} inCard />
      ))}
    </div>
  );
}

// Mirror mode - the screen described its own controls, so they are rendered directly rather than as
// arrows. There is no injected Back: a mirrored screen includes its own back control the same way it
// does on the TV, so there is never a second one to keep in sync.
//
// Controls that read as a list of settings - switchers, checkboxes, selects - are grouped into one
// card with hairline dividers, which is what keeps a settings screen from reading as a stack of
// loose slabs. Buttons and the stepper stand on their own, because each is an action rather than a
// row of a list.
export function MirrorKeyboard({ keyboard }: { keyboard: HelpEntry }) {
  const currentSong = useSubscription('current-song');
  const [playerNumber] = useEventListener(events.remoteMicPlayerSet, true) ?? [null];
  const controls = renderableControls(keyboard.controls ?? []);
  const titleIcon = keyboard.icon ? remoteButtonIcons[keyboard.icon] : 'ic:baseline-keyboard';

  const showMicCircle = mirrorDrawsItsOwnMicCircle(keyboard, currentSong?.started === true);
  const micColour =
    playerNumber !== null ? styles.colors.players[playerNumber].perfect.fill : styles.colors.lines.normal.fill;

  // Rule B: a screen names the action it exists for, or it has none. Nothing is promoted by position
  // any more. The old rule took the first non-back button, which was merely wrong on a screen of
  // equal choices (Settings, Manage Songs, Singers, Queue), dangerous on Setup microphones - where
  // it landed on a destructive action - and worst on Vocal range's measuring step, where
  // registration order is back, skip, use, so `Skip this step` took the fill and the action the
  // screen exists for sat under it as an ordinary row.
  const primaryName = keyboard.primary;

  // `group` takes precedence where a screen sends one. The run-of-value-controls rule below then
  // applies only to the controls without one, which is every other mirrored screen.
  const slots: Array<{ group: ControlGroup; controls: typeof controls }> = [];
  const ungrouped: typeof controls = [];
  for (const control of controls) {
    if (!control.group) {
      ungrouped.push(control);
      continue;
    }

    const existing = slots.find((slot) => slot.group.id === control.group!.id);
    if (existing) existing.controls.push(control);
    else slots.push({ group: control.group, controls: [control] });
  }

  // Your own slot first, decided here rather than on the host: one layout is published to every
  // phone, so only the phone knows which of the slots is the one it is sitting in. `sort` is stable,
  // so everything else keeps the order the screen registered it in.
  const isSelf = (group: ControlGroup) => group.player !== undefined && group.player === playerNumber;
  slots.sort((left, right) => Number(isSelf(right.group)) - Number(isSelf(left.group)));

  // Rule C: the way out belongs at the thumb end, and it should not be something you scroll to find.
  // What pins is the way OUT rather than the primary - a declared primary is often the first thing on
  // the screen (Sing a song, Resume song), and moving it to the foot would put the action under
  // everything it is the point of. Setup microphones is unaffected either way: its `Done` is both.
  //
  // It is the control that BOOKENDS the list that qualifies, because a genuine way out registers
  // either first (a header ←) or last (a footer Back). A `back` variant in the MIDDLE of the list is
  // wearing the muted treatment for its looks and belongs among the rows it registered with - High
  // scores' `Stop sharing scores` is the one in the app today, and pinning that to the foot would put
  // it under the button that leaves the screen.
  const backIndex = ungrouped.findIndex((control) => control.type === 'button' && control.variant === 'back');
  const pinned =
    backIndex !== -1 && (backIndex === 0 || backIndex === ungrouped.length - 1) ? ungrouped[backIndex] : undefined;
  const inFlow = pinned ? ungrouped.filter((control) => control !== pinned) : ungrouped;

  const groups: Array<{ card: boolean; controls: typeof controls }> = [];
  for (const control of inFlow) {
    // Rule A: everything carrying a VALUE cards together. `text` and `input-lag` belong beside
    // switch, checkbox and select for the reason those three do - a row that changes a value is a row
    // of a list, not an action. Left out, the pause menu's input-lag stepper stood in the same stack
    // as four navigations, so the one control that changes a value looked like one that leaves.
    const inCard =
      control.type === 'switch' ||
      control.type === 'checkbox' ||
      control.type === 'select' ||
      control.type === 'text' ||
      control.type === 'input-lag';
    const last = groups.at(-1);
    if (last && last.card && inCard) last.controls.push(control);
    else groups.push({ card: inCard, controls: [control] });
  }

  // Rule C again: past about six controls the screen scrolls, and what should scroll is the actions -
  // the header and the settings card above them hold still rather than being the first thing carried
  // off the top. Only a SHORT leading card is held: on Select Song Languages the leading card is every
  // language there is, and holding that would fill the screen with the very thing being scrolled. Not
  // on a grouped screen either - Setup microphones' four slot cards ARE the content, not a preamble.
  const heldCard =
    slots.length === 0 &&
    controls.length > CROWDED_CONTROLS &&
    groups[0]?.card &&
    groups[0].controls.length <= HELD_CARD_ROWS
      ? groups[0]
      : undefined;
  const flowGroups = heldCard ? groups.slice(1) : groups;

  return (
    <motion.div
      data-test="remote-keyboard"
      data-mode="mirror"
      className="flex h-full min-h-0 w-full flex-col gap-3"
      {...screenTransition}>
      {keyboard.title && <ScreenHeader icon={titleIcon} title={keyboard.title} subtitle={keyboard.subtitle} />}
      {/* Held above the scroller rather than inside it, so the settings it holds stay on screen
          while the actions under them pass by. */}
      {heldCard && (
        <div data-test="mirror-held-card">
          <ValueCard controls={heldCard.controls} />
        </div>
      )}
      {showMicCircle && (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <div className="relative flex size-42 items-center justify-center rounded-full bg-black/40 shadow-[inset_0_0_0_2px_rgba(255,255,255,0.18)]">
            <span
              aria-hidden
              className="absolute -inset-2.5 animate-pulse rounded-full opacity-45"
              style={{ boxShadow: `0 0 0 2px ${micColour}` }}
            />
            <Icon icon="ic:baseline-mic" size={21} style={{ color: micColour }} />
          </div>
        </div>
      )}
      <div
        data-test="mirror-scroll"
        className={`flex flex-col overflow-y-auto overscroll-contain ${showMicCircle ? 'shrink-0' : 'min-h-0 flex-1'}`}>
        {/* Centred with an auto margin rather than `justify-center`, for the reason `menu-with-logo`
            gives: once the controls are taller than the box, centring overflows in BOTH directions
            and the first card can neither be seen nor scrolled to, while an auto margin collapses to
            nothing and the list simply scrolls from the top. Four slot cards is enough to reach
            that, which loose rows never were. */}
        <div className="my-auto flex flex-col gap-2.5 pb-1">
          {/* Above the rows rather than between them, which is where the board drew it: it is the
              one block on a mirrored screen that is NOT a mirror of a television control, and a
              phone-local button sitting in the middle of the mirrored list reads as one. */}
          {keyboard.photo && <PhonePhoto photo={keyboard.photo} />}
          {/* Above the controls: it says what the screen is showing, and the one or two controls
              under it are what can be done about it. */}
          {keyboard.onTelevision && <OnTelevision icon={titleIcon} />}
          {slots.map(({ group, controls: slotControls }) => (
            <div
              key={group.id}
              data-test={`control-group-${group.id}`}
              className="flex shrink-0 flex-col overflow-hidden rounded-[1.125rem] bg-black/55"
              style={{ boxShadow: `inset 0 0 0 1px ${group.colour ?? 'rgba(255,255,255,0.14)'}` }}>
              <div className="flex items-center gap-2 px-4.5 pt-3 pb-2">
                <span
                  className="min-w-0 flex-1 truncate text-[0.9375rem] font-bold tracking-[0.08em] uppercase"
                  style={{ color: group.colour }}>
                  {group.label}
                </span>
                {isSelf(group) && (
                  <span className="bg-active shrink-0 rounded px-1.5 py-0.5 text-[0.8125rem] font-black text-black">
                    YOU
                  </span>
                )}
              </div>
              <div className="flex flex-col [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-white/10">
                {slotControls.map((control) => (
                  <RemoteControl
                    key={control.name}
                    control={control}
                    onActivate={activateControl}
                    inCard
                    // The one press on this screen that ends your own game. On the television it is
                    // an unremarkable ✕, because whoever holds the remote is never the one removed.
                    confirm={
                      isSelf(group) && control.type === 'button' && control.variant === 'destructive'
                        ? {
                            title: `Stop being ${group.label}?`,
                            description:
                              'This phone stops singing and stays a remote for the television. Anything already sung tonight is unaffected.',
                            action: 'Leave the slot',
                          }
                        : undefined
                    }
                  />
                ))}
              </div>
            </div>
          ))}
          {flowGroups.map((group, index) =>
            group.card ? (
              <ValueCard key={index} controls={group.controls} />
            ) : (
              group.controls.map((control) => (
                <RemoteControl
                  key={control.name}
                  control={control}
                  onActivate={activateControl}
                  primary={control.name === primaryName}
                />
              ))
            ),
          )}
        </div>
      </div>
      {pinned && (
        <div className="shrink-0" data-test="mirror-pinned">
          <RemoteControl control={pinned} onActivate={activateControl} primary={pinned.name === primaryName} />
        </div>
      )}
    </motion.div>
  );
}
